import { useEffect, useState } from 'react';
import loadBackupList, { BackupListType } from '../api/loader/loadBackupList';
import SettingsNavigation from '../components/SettingsNavigation';
import updateTaskByName from '../api/actions/updateTaskByName';
import queueBackup from '../api/actions/queueBackup';
import restoreBackup from '../api/actions/restoreBackup';
import Notifications from '../components/Notifications';
import Button from '../components/Button';
import { ApiResponseType } from '../functions/APIClient';
import ToggleConfig from '../components/ToggleConfig';
import queueStartFilesystemRescan from '../api/actions/queueStartFilesystemRescan';
import queueManualImport from '../api/actions/queueManualImport';

const SettingsActions = () => {
  const [deleteIgnored, setDeleteIgnored] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [processingImports, setProcessingImports] = useState(false);
  const [reSyncMeta, setReSyncMeta] = useState(false);
  const [backupStarted, setBackupStarted] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const [reScanningFileSystem, setReScanningFileSystem] = useState(false);
  const [rescanIgnoreErrors, setRescanIgnoreErrors] = useState(false);
  const [rescanPreferLocal, setRescanPreferLocal] = useState(false);
  const [manualPreferLocal, setManualPreferLocal] = useState(false);
  const [manualIgnoreErrors, setManualIgnoreErrors] = useState(false);

  const [backupListResponse, setBackupListResponse] = useState<ApiResponseType<BackupListType>>();

  const { data: backupListResponseData } = backupListResponse ?? {};

  const backups = backupListResponseData;
  const hasBackups = !!backups && backups?.length > 0;

  useEffect(() => {
    (async () => {
      const backupListResponse = await loadBackupList();

      setBackupListResponse(backupListResponse);
    })();
  }, []);

  return (
    <>
      <title>NM | Actions</title>
      <div className="boxed-content">
        <SettingsNavigation />
        <Notifications
          pageName={'all'}
          update={
            deleteIgnored ||
            deletePending ||
            processingImports ||
            reSyncMeta ||
            backupStarted ||
            isRestoringBackup ||
            reScanningFileSystem
          }
          setShouldRefresh={() => {
            setDeleteIgnored(false);
            setDeletePending(false);
            setProcessingImports(false);
            setReSyncMeta(false);
            setBackupStarted(false);
            setIsRestoringBackup(false);
            setReScanningFileSystem(false);
          }}
        />

        <div className="title-bar">
          <h1>Actions</h1>
        </div>
        <div className="settings-group">
          <h2>Manual media files import.</h2>
          <p>
            Add files to the <span className="settings-current">cache/import</span> folder. Make
            sure to follow the instructions in the{' '}
            <a
              href="#"
              target="_blank"
            >
              Docs
            </a>
            .
          </p>
          <div id="manual-import">
            <div className="settings-box-wrapper">
              <div>
                <p>Prefer embedded metadata</p>
              </div>
              <ToggleConfig
                name="manual_prefer_local"
                value={manualPreferLocal}
                updateCallback={() => setManualPreferLocal(!manualPreferLocal)}
              />
            </div>
            <div className="settings-box-wrapper">
              <div>
                <p>Ignore missing metadata errors</p>
              </div>
              <ToggleConfig
                name="manual_ignore_error"
                value={manualIgnoreErrors}
                updateCallback={() => setManualIgnoreErrors(!manualIgnoreErrors)}
              />
            </div>
            {processingImports && <p>Processing import</p>}
            {!processingImports && (
              <Button
                label="Start import"
                onClick={async () => {
                  await queueManualImport(manualIgnoreErrors, manualPreferLocal);
                  setProcessingImports(true);
                }}
              />
            )}
          </div>
        </div>
        <div className="settings-group">
          <h2>Embed metadata into media file</h2>
          <p>Embed metadata into media files as mp4 tags.</p>
          <div id="re-meta">
            {reSyncMeta && <p>Processing video metadata</p>}
            {!reSyncMeta && (
              <Button
                label="Start process"
                onClick={async () => {
                  await updateTaskByName('resync_metadata');
                  setReSyncMeta(true);
                }}
              />
            )}
          </div>
        </div>
        <div className="settings-group">
          <h2>ZIP file index backup</h2>
          <p>
            Export your database to a zip file stored at{' '}
            <span className="settings-current">cache/backup</span>.
          </p>
          <p>
            <i>
              Zip file backups are very slow for large archives and consistency is not guaranteed,
              use snapshots instead. Make sure no other tasks are running when creating a Zip file
              backup.
            </i>
          </p>
          <div id="db-backup">
            {backupStarted && <p>Backing up archive</p>}
            {!backupStarted && (
              <Button
                label="Start backup"
                onClick={async () => {
                  await queueBackup();
                  setBackupStarted(true);
                }}
              />
            )}
          </div>
        </div>
        <div className="settings-group">
          <h2>Restore from backup</h2>
          <p>
            <span className="danger-zone">Danger Zone</span>: This will replace your existing index
            with the backup.
          </p>
          <p>
            Restore from available backup files from{' '}
            <span className="settings-current">cache/backup</span>.
          </p>
          {!hasBackups && <p>No backups found.</p>}
          {hasBackups && (
            <>
              <div className="backup-grid-row">
                <span></span>
                <span>Timestamp</span>
                <span>Source</span>
                <span>Filename</span>
              </div>
              {isRestoringBackup && <p>Restoring from backup</p>}
              {!isRestoringBackup &&
                backups.map(backup => {
                  return (
                    <div key={backup.filename} id={backup.filename} className="backup-grid-row">
                      <Button
                        label="Restore"
                        onClick={async () => {
                          await restoreBackup(backup.filename);
                          setIsRestoringBackup(true);
                        }}
                      />
                      <span>{backup.timestamp}</span>
                      <span>{backup.reason}</span>
                      <span>{backup.filename}</span>
                    </div>
                  );
                })}
            </>
          )}
        </div>
        <div className="settings-group">
          <h2>Rescan filesystem</h2>
          <p>
            <span className="danger-zone">Danger Zone</span>: This will delete the metadata of
            deleted videos from the filesystem.
          </p>
          <p>
            Rescan your media folder looking for missing videos and clean up index. More info on the{' '}
            <a
              href="#"
              target="_blank"
            >
              Docs
            </a>
            .
          </p>
          <div id="fs-rescan">
            <div className="settings-box-wrapper">
              <div>
                <p>Prefer embedded metadata</p>
              </div>
              <ToggleConfig
                name="prefer_local"
                value={rescanPreferLocal}
                updateCallback={() => setRescanPreferLocal(!rescanPreferLocal)}
              />
            </div>
            <div className="settings-box-wrapper">
              <div>
                <p>Ignore missing metadata errors</p>
              </div>
              <ToggleConfig
                name="ignore_error"
                value={rescanIgnoreErrors}
                updateCallback={() => setRescanIgnoreErrors(!rescanIgnoreErrors)}
              />
            </div>
            {reScanningFileSystem && <p>File system scan in progress</p>}
            {!reScanningFileSystem && (
              <Button
                label="Rescan filesystem"
                onClick={async () => {
                  await queueStartFilesystemRescan(rescanIgnoreErrors, rescanPreferLocal);
                  setReScanningFileSystem(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsActions;
